import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowUpRight, Twitter, Linkedin, Github } from 'lucide-react';

/**
 * Creative mobile menu used across Astra's public surfaces.
 *
 * Consists of two pieces:
 *  - AstraMobileMenuButton : animated morphing hamburger <-> X
 *  - AstraMobileMenu       : full-screen glass overlay with huge staggered nav
 *
 * Theming comes in via a tiny `theme` prop so it can piggyback on either
 * AstraShell's ASTRA_THEME or AstraLandingPage's THEME object.
 */

const DEFAULT_NAV = [
  { label: 'Product', href: '/#product' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'Widgets & API', href: '/widgets-api' },
  { label: 'Features', href: '/features' },
  { label: 'Live Chart', href: '/trading', external: true },
];

export function AstraMobileMenuButton({ open, onClick, theme }) {
  const t = theme || { bgCard: '#0c0c10', text: '#f8fafc' };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={open ? 'Close menu' : 'Open menu'}
      aria-expanded={open}
      data-testid="mobile-menu-btn"
      className="md:hidden relative w-11 h-11 rounded-full flex items-center justify-center transition-all active:scale-95"
      style={{
        backgroundColor: open ? 'rgba(59,130,246,0.14)' : `${t.bgCard}cc`,
        border: `1px solid ${open ? 'rgba(59,130,246,0.35)' : 'rgba(255,255,255,0.08)'}`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        transition: 'all 260ms cubic-bezier(0.4,0,0.2,1)',
      }}
    >
      <span
        className="absolute block w-4 h-[1.5px] rounded-full"
        style={{
          backgroundColor: open ? '#60a5fa' : t.text,
          transform: open ? 'translateY(0) rotate(45deg)' : 'translateY(-5px)',
          transition: 'transform 320ms cubic-bezier(0.4,0,0.2,1), background-color 200ms',
        }}
      />
      <span
        className="absolute block w-4 h-[1.5px] rounded-full"
        style={{
          backgroundColor: open ? 'transparent' : t.text,
          opacity: open ? 0 : 1,
          transform: open ? 'scaleX(0)' : 'scaleX(1)',
          transition: 'opacity 180ms, transform 220ms cubic-bezier(0.4,0,0.2,1)',
        }}
      />
      <span
        className="absolute block w-4 h-[1.5px] rounded-full"
        style={{
          backgroundColor: open ? '#60a5fa' : t.text,
          transform: open ? 'translateY(0) rotate(-45deg)' : 'translateY(5px)',
          transition: 'transform 320ms cubic-bezier(0.4,0,0.2,1), background-color 200ms',
        }}
      />
    </button>
  );
}

export function AstraMobileMenu({
  open,
  onClose,
  navItems = DEFAULT_NAV,
  ctaLabel = 'Get started',
  ctaHref = '/register',
  signInHref = '/login',
  theme,
}) {
  const navigate = useNavigate();
  const t = theme || { bg: '#050507', bgCard: '#0c0c10', bgElevated: '#111116', text: '#f8fafc', textMuted: '#52525b', textSecondary: '#a1a1aa' };

  // Lock body scroll + ESC to close
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  const go = (item) => {
    onClose?.();
    if (item.external) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    } else if (item.href.startsWith('/')) {
      navigate(item.href);
    } else {
      window.location.href = item.href;
    }
  };

  return (
    <div
      className="md:hidden fixed inset-0 z-[60]"
      style={{
        pointerEvents: open ? 'auto' : 'none',
        visibility: open ? 'visible' : 'hidden',
        transition: 'visibility 0s linear ' + (open ? '0s' : '420ms'),
      }}
      data-testid="mobile-menu-root"
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="absolute inset-0"
        style={{
          backgroundColor: 'rgba(5,5,10,0.72)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          opacity: open ? 1 : 0,
          transition: 'opacity 380ms cubic-bezier(0.4,0,0.2,1)',
        }}
      />

      {/* Panel */}
      <div
        className="absolute inset-0 flex flex-col"
        style={{
          transform: open ? 'translateY(0)' : 'translateY(-12px)',
          opacity: open ? 1 : 0,
          transition: 'transform 420ms cubic-bezier(0.22,1,0.36,1), opacity 320ms',
        }}
      >
        {/* Radial accent glow behind content */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 85% 0%, rgba(59,130,246,0.22) 0%, transparent 55%), radial-gradient(ellipse at 0% 100%, rgba(14,165,233,0.14) 0%, transparent 50%)' }} />
        {/* Subtle grain */}
        <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

        {/* Top bar — brand + close */}
        <div className="relative flex items-center justify-between px-5 pt-5 pb-3">
          <Link to="/" onClick={onClose} className="flex items-center gap-2" data-testid="mobile-menu-brand">
            <img src="/assets/astra-logo-full.png" alt="AstraTerminal" className="h-9 object-contain" style={{ width: 'auto' }} />
          </Link>
          <AstraMobileMenuButton open={true} onClick={onClose} theme={theme} />
        </div>

        {/* Giant nav items with staggered entrance */}
        <nav className="relative flex-1 flex flex-col justify-center px-6 gap-1">
          {navItems.map((item, i) => (
            <button
              key={item.label}
              onClick={() => go(item)}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              className="group w-full flex items-center justify-between py-3.5 border-b"
              style={{
                borderColor: 'rgba(255,255,255,0.06)',
                transform: open ? 'translateY(0)' : 'translateY(18px)',
                opacity: open ? 1 : 0,
                transition: `transform 520ms cubic-bezier(0.22,1,0.36,1) ${120 + i * 70}ms, opacity 420ms ease ${120 + i * 70}ms`,
              }}
            >
              <span className="flex items-baseline gap-3">
                <span className="text-[11px] font-mono tabular-nums tracking-widest" style={{ color: 'rgba(96,165,250,0.85)' }}>
                  0{i + 1}
                </span>
                <span
                  className="text-3xl font-semibold tracking-tight text-left"
                  style={{
                    color: t.text,
                    background: 'linear-gradient(90deg, #ffffff 0%, #ffffff 100%)',
                    WebkitBackgroundClip: 'text',
                    transition: 'transform 280ms cubic-bezier(0.22,1,0.36,1)',
                  }}
                >
                  {item.label}
                </span>
              </span>
              <ArrowUpRight
                className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                style={{ color: 'rgba(255,255,255,0.45)' }}
              />
            </button>
          ))}
        </nav>

        {/* Bottom — CTA + sign in + social */}
        <div className="relative px-6 pb-8 pt-4"
             style={{
               transform: open ? 'translateY(0)' : 'translateY(18px)',
               opacity: open ? 1 : 0,
               transition: `transform 520ms cubic-bezier(0.22,1,0.36,1) ${120 + navItems.length * 70}ms, opacity 420ms ease ${120 + navItems.length * 70}ms`,
             }}>
          <div className="flex items-center gap-2 mb-5">
            <button
              onClick={() => { onClose?.(); navigate(ctaHref); }}
              data-testid="mobile-cta"
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm font-semibold"
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: '#fff',
                boxShadow: '0 8px 28px rgba(59,130,246,0.32), inset 0 1px 0 rgba(255,255,255,0.2)',
              }}
            >
              {ctaLabel}
            </button>
            <button
              onClick={() => { onClose?.(); navigate(signInHref); }}
              data-testid="mobile-signin"
              className="px-5 py-3.5 rounded-full text-sm font-medium"
              style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: t.text }}
            >
              Sign in
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {[Twitter, Linkedin, Github].map((Icon, idx) => (
                <a key={idx} href="#" className="w-9 h-9 rounded-full flex items-center justify-center"
                   style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <Icon className="h-3.5 w-3.5" style={{ color: t.textSecondary }} />
                </a>
              ))}
            </div>
            <span className="text-[11px] tracking-wider uppercase" style={{ color: t.textMuted }}>
              © {new Date().getFullYear()} Astra
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
